/**
 * Sorting for a staging bucket (Ready To Route). The bucket holds unassigned
 * orders in no meaningful sequence, so the user can re-order them by a handful of
 * order attributes to make the pile easier to scan. Depot HUB stops stay pinned at
 * their positions; only the real orders between them are reordered.
 */

export type BucketSortKey = 'time' | 'shipment' | 'vehicleType' | 'serviceType' | 'orderType'

export const BUCKET_SORT_OPTIONS: { key: BucketSortKey; label: string }[] = [
  { key: 'time', label: 'Time' },
  { key: 'shipment', label: 'Shipment #' },
  { key: 'vehicleType', label: 'Vehicle type' },
  { key: 'serviceType', label: 'Service type' },
  { key: 'orderType', label: 'Order type' },
]

const str = (v: any): string => String(v ?? '').trim()

// Undated stops sort last rather than jumping to the front on an invalid date.
const timeVal = (s: any): number => {
  const t = Date.parse(s?.startWindow)
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
}

const comparator = (key: BucketSortKey) => (a: any, b: any): number => {
  switch (key) {
    case 'time':
      return timeVal(a) - timeVal(b)
    case 'shipment':
      // Invoices mix letters + numbers ("S-ORD114389") — numeric-aware collation
      // orders 2 before 10.
      return str(a.invoice).localeCompare(str(b.invoice), undefined, { numeric: true, sensitivity: 'base' })
    case 'vehicleType':
      return str(a.vehicleType).localeCompare(str(b.vehicleType))
    case 'serviceType':
      return str(a.serviceType).localeCompare(str(b.serviceType))
    case 'orderType':
      return str(a.orderType).localeCompare(str(b.orderType))
    default:
      return 0
  }
}

/**
 * Sort a route's non-HUB stops by `key`, leaving HUB depot stops pinned in place.
 * Array.prototype.sort is stable, so equal keys keep their prior relative order.
 */
export const sortStopsPreservingHubs = (stops: any[], key: BucketSortKey): any[] => {
  const sortedNonHubs = (stops || []).filter((s) => s && s.orderType !== 'HUB').sort(comparator(key))
  let i = 0
  return (stops || []).map((s) => (s && s.orderType !== 'HUB' ? sortedNonHubs[i++] : s))
}
